import { useToast } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import type {
  SelectionCategories,
  SelectionList,
  TreeItem,
  ValidValueTypes,
} from '@revelio/iso-utility';

import { SelectionListControls } from '../../../selection-lists/components/SelectionListControls/SelectionListControls';
import type { TreeApi } from '../useTreeApi';
import type { BreadcrumbTreeProps } from './BreadcrumbTree';
import styles from './BreadcrumbTreeFilter.module.css';
import { BreadcrumbTreeSelection } from './BreadcrumbTreeSelection';

type BreadcrumbTreeSelectionWithControlsProps = Pick<
  BreadcrumbTreeProps,
  'branches'
> & {
  selectionListIds: SelectionCategories[];
  initialSelections: Record<string, TreeItem>;
  selectionLists: SelectionList<ValidValueTypes>[];
  renderWrapper: (props: { tree: ReactNode; controls: ReactNode }) => ReactNode;
  maxSelections?: number;
  filterLabel?: string;
  onClose?: () => void;
  onSubmit?: (items: Record<string, TreeItem>) => void;
  setNumSelections?: (num: number) => void;
  isSelectionListIdsEnabled?: boolean;
};

export const BreadcrumbTreeSelectionWithControls = ({
  initialSelections = {},
  branches,
  selectionLists,
  maxSelections,
  filterLabel,
  onClose,
  onSubmit,
  setNumSelections,
  renderWrapper,
  isSelectionListIdsEnabled,
}: BreadcrumbTreeSelectionWithControlsProps) => {
  const toast = useToast();
  const treeRef = useRef<TreeApi>(null);
  const [selections, setSelections] =
    useState<Record<string, TreeItem>>(initialSelections);

  useEffect(() => {
    if (setNumSelections) {
      setNumSelections(Object.keys(selections).length);
    }
  }, [selections, setNumSelections]);

  return renderWrapper({
    tree: (
      <BreadcrumbTreeSelection
        ref={treeRef}
        initialSelections={initialSelections}
        branches={branches}
        selectionLists={selectionLists}
        maxSelections={maxSelections}
        isSelectionListIdsEnabled={isSelectionListIdsEnabled}
        onMaxExceeded={() => {
          if (!toast.isActive('max-selections-toast')) {
            toast({
              id: 'max-selections-toast',
              position: 'top-right',
              status: 'warning',
              title: `${filterLabel ? filterLabel : 'Selection'} Limit`,
              description: `You can only choose up to ${maxSelections} ${filterLabel ? filterLabel : 'items'} at a time!`,
              isClosable: true,
              variant: 'subtle',
            });
          }
        }}
        onChange={setSelections}
      />
    ),
    controls: (
      <SelectionListControls
        className={styles.controls}
        onClear={() => {
          setSelections({});
          treeRef.current?.clearAll();
        }}
        onClose={onClose}
        onSubmit={() => onSubmit?.(selections)}
        clearText="Reset"
        submitText="Submit"
        isSubmitDisabled={isEmpty(selections)}
      />
    ),
  });
};
